'use client';

import React, {
  type MouseEvent,
} from 'react';
import styled from 'styled-components';

import useTranslation from '../../translation/useTranslation';
import { useNotifications } from './state';
import { NotificationDto } from './types';

export interface EntryProps {
  notification: NotificationDto
}

function Entry({
  notification,
}: EntryProps) {
  const {
    id, isRead, url, publishedAt, descriptionNo, descriptionEn, subjectEn, subjectNo,
  } = notification;
  const { t, language } = useTranslation();
  const { markEventAsRead } = useNotifications();

  const description = language === 'no' ? descriptionNo : descriptionEn;
  const subject = language === 'no' ? subjectNo : subjectEn;

  const day = new Date(publishedAt)
    .toLocaleDateString(language, {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    });

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (!isRead) {
      event.preventDefault();
      markEventAsRead(id).then(() => {
        window.location.assign(url);
      });
    }
  }

  if (!url) {
    return (
      <EntryBase
        id={id}
        as="div"
        onMouseEnter={() => {
          if (!isRead) {
            markEventAsRead(id);
          }
        }}
      >
        <Day>{day}</Day>
        <Description>
          {description || subject}
        </Description>
        {!isRead && (
          <UnreadIndicator>
            {t('notification.unread')}
          </UnreadIndicator>
        )}
      </EntryBase>
    );
  }

  return (
    <EntryBase
      id={id}
      href={url}
      onClick={
        (event: MouseEvent<HTMLAnchorElement>) => {
          handleClick(event);
        }
      }
    >
      <Day>{day}</Day>
      <Description>
        {description || subject}
      </Description>
      <Arrow>arrow-right</Arrow>
      {!isRead && (
        <UnreadIndicator>
          {t('notification.unread')}
        </UnreadIndicator>
      )}
    </EntryBase>
  );
}

export default Entry;

export const Description = styled.p`
  grid-area: description;

  color: inherit;
  font-size: 16px;
`;

export const EntryBase = styled.a`
  position: relative;

  display: grid;
  grid-template-columns: 1fr 24px;
  grid-template-rows: 16px 1fr;
  grid-template-areas:
    "date ."
    "description arrow"
  ;
  grid-gap: 12px;
  
  text-decoration: none;

  font-size: 16px;
  line-height: 24px;

  padding: 16px;
  width: 100%;
  box-sizing: border-box;

  color: var(--navbar-toggle-accent-color, #700);
  background: var(--navbar-toggle-accent-background, #FFF0F0);

  border-radius: 8px;

  &:hover {
    color: var(--navbar-toggle-accent-color, #700);
    background: var(--navbar-toggle-accent-background-hover, #FFDFDF);

    & > ${Description} {
      text-decoration: underline;
    }
  }

  &:focus-visible {
    outline-offset: 2px;
    outline: 2px solid var(--navbar-focus, #fff);
  }

  ${({ as }) => as === 'div' && `
    &:hover {
      color: var(--navbar-toggle-accent-color, #700);
      background: var(--navbar-toggle-accent-background, #FFF0F0);

      > ${Description} {
        text-decoration: none;
      }
    }
  `}
`;

export const Day = styled.div`
  grid-area: date;

  line-height: 16px;
  font-size: 12px;
  font-weight: 600;

  text-transform: uppercase;
`;

export const Arrow = styled.i`
  grid-area: arrow;

  font-weight: 600;
  line-height: 24px;
`;

export const UnreadIndicator = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;

  font-size: 12px;
  line-height: 12px;
  font-weight: 600;

  padding: 4px 8px;
  border-radius: 12px;

  color: var(--navbar-toggle-accent-background, #FFF0F0);
  background: var(--navbar-toggle-accent-color, #700);
`;
